import { supabase } from "../utils/supabaseClient";
import { toast } from "sonner";

// Stripe product and price IDs from your Stripe account
// TODO: Replace these with your actual Stripe Price IDs from the new products you create
const STRIPE_PRODUCTS = {
  premium_monthly: {
    priceId: "price_1SVmpZLEwMxFpWxPy5ZeYoyy",
    name: "Bramlo Premium",
    description:
      "Monthly subscription to Bramlo Premium - All features unlocked",
  },
  premium_annual: {
    priceId: "price_1UIHHfLEwMxFpWxPkT9Y7dO3",
    name: "Bramlo Premium Annual",
    description: "Annual subscription to Bramlo Premium - Best value",
  },
  test: {
    priceId: "test_0", // Test subscription - $0.00
    name: "Test Plan",
    description: "Test subscription for development",
  },
};

// Direct payment links for all plans
// TODO: Create payment links in Stripe Dashboard and replace these URLs
const STRIPE_PAYMENT_LINKS = {
  test: "https://buy.stripe.com/test_REPLACE_ME",
  premium_monthly: "https://buy.stripe.com/fZu7sMcmlaxb5CP1I1cQU08",
  premium_annual: "https://buy.stripe.com/eVq9AU4TT34J1mzcmFcQU0f",
};

/**
 * Create a checkout session with Stripe via Supabase Edge Function
 * @param {string} planId - The ID of the plan to subscribe to
 * @param {string} userId - The ID of the user subscribing
 * @returns {Promise<string>} - The URL to redirect to for checkout
 */
export const createCheckoutSession = async (planId, userId) => {
  try {
    if (!planId || !STRIPE_PRODUCTS[planId]) {
      throw new Error(`Invalid plan ID: ${planId}`);
    }

    if (!userId) {
      throw new Error("User ID is required");
    }

    // Use direct payment links for all plans for now
    if (STRIPE_PAYMENT_LINKS[planId]) {
      return STRIPE_PAYMENT_LINKS[planId];
    }

    const { data: user } = await supabase.auth.getUser();
    if (!user || !user.user) {
      throw new Error("User not authenticated");
    }

    const userEmail = user.user.email;

    // Get the price ID for the plan
    const { priceId } = STRIPE_PRODUCTS[planId];

    // Call the Supabase Edge Function to create a checkout session
    const { data, error } = await supabase.functions.invoke("create-checkout", {
      body: {
        price_id: priceId,
        user_id: userId,
        return_url: `${window.location.origin}/dashboard/stripe-success`,
      },
      headers: {
        "X-Customer-Email": userEmail,
      },
    });

    if (error) {
      console.error("Error creating checkout session:", error);
      throw new Error(`Failed to create checkout session: ${error.message}`);
    }

    if (!data || !data.url) {
      throw new Error("No checkout URL returned");
    }

    return data.url;
  } catch (error) {
    console.error("Error in createCheckoutSession:", error);
    toast.error(`Payment error: ${error.message}`);
    throw error;
  }
};

/**
 * Redirect to Stripe checkout for the selected plan
 * @param {string} planId - The ID of the plan to subscribe to
 */
export const redirectToPayment = async (planId) => {
  try {
    const { data: user } = await supabase.auth.getUser();

    if (!user || !user.user) {
      toast.error("You must be logged in to subscribe");
      return false;
    }

    // Create a toast ID so we can dismiss it later
    const toastId = toast.loading("Preparing checkout...");

    const checkoutUrl = await createCheckoutSession(planId, user.user.id);

    if (!checkoutUrl) {
      // Dismiss the loading toast and show error
      toast.dismiss(toastId);
      toast.error("Failed to create checkout session");
      return false;
    }

    // Dismiss the loading toast
    toast.dismiss(toastId);

    // Open in a new tab instead of redirecting
    window.open(checkoutUrl, "_blank");

    // Show a success toast to guide the user
    toast.success("Payment page opened in a new tab");

    // Return true to indicate success
    return true;
  } catch (error) {
    console.error("Error redirecting to payment:", error);
    toast.error(`Payment error: ${error.message}`);
    return false;
  }
};

/**
 * Get the subscription for a user
 * @param {string} userId - The ID of the user to get the subscription for
 * @returns {Promise<Object|null>} - The subscription object or null if not found
 */
export const getUserSubscription = async (userId) => {
  try {
    if (!userId) {
      return { status: "none", user_id: "unknown" };
    }

    // Query the subscriptions table for the user's subscription
    const { data, error } = await supabase
      .from("subscriptions")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(1)
      .single();

    if (error) {
      // If the error is that no rows were returned, the user doesn't have a subscription
      if (error.code === "PGRST116") {
        // Return an empty subscription object instead of null
        return { status: "none", user_id: userId };
      }
      // Don't throw the error, just return a default object
      return { status: "error", user_id: userId };
    }

    return data;
  } catch (error) {
    // Return an empty subscription object instead of null
    return { status: "error", user_id: userId || "unknown" };
  }
};

/**
 * Check if a user has an active subscription
 * @param {string} userId - The ID of the user to check
 * @returns {Promise<boolean>} - Whether the user has an active subscription
 */
export const hasActiveSubscription = async (userId) => {
  const subscription = await getUserSubscription(userId);

  // If subscription is null or has status 'none' or 'error', return false
  if (
    !subscription ||
    subscription.status === "none" ||
    subscription.status === "error"
  ) {
    return false;
  }

  // Check if subscription is active or trialing
  const isActive =
    subscription.status === "active" || subscription.status === "trialing";

  return isActive;
};

/**
 * Get the plan details for a plan ID
 * @param {string} planId - The ID of the plan to get details for
 * @returns {Object} - The plan details
 */
export const getPlanDetails = (planId) => {
  const plans = {
    premium_monthly: {
      name: "Bramlo Premium",
      price: 9.99,
      period: "month",
      features: [
        "Unlimited AI conversations with ChatManager",
        "All AI models (GPT-4, Claude, Gemini)",
        "8 prompt templates for instant productivity",
        "Advanced chat settings & controls",
        "Unlimited notes with RichNotesEditor",
        "6 note templates (Meeting, Lecture, Research, etc.)",
        "Auto-save & version history",
        "Smart search & organization",
        "Export everything (Markdown, Text, JSON)",
        "Analytics dashboard",
        "Priority support",
      ],
    },
    premium_annual: {
      name: "Bramlo Premium Annual",
      price: 49.99,
      period: "year",
      features: [
        "Everything in Premium Monthly",
        "Save ~$70/year (58% discount)",
        "Unlimited AI conversations",
        "All premium features",
        "Priority support",
        "All future features included",
        "Best value for serious learners",
      ],
    },
    test: {
      name: "Test Plan",
      price: 0.0,
      period: "month",
      features: ["Test subscription for development"],
    },
  };

  return plans[planId] || null;
};

/**
 * Get all available subscription plans
 * @returns {Promise<Array>} - Array of available plans
 */
export const getAvailablePlans = async () => {
  try {
    // Call the Supabase Edge Function to get available plans
    const { data, error } = await supabase.functions.invoke("get-plans");

    if (error) {
      console.error("Error fetching plans:", error);
      throw new Error(`Failed to fetch plans: ${error.message}`);
    }

    return data || [];
  } catch (error) {
    console.error("Error in getAvailablePlans:", error);
    return [];
  }
};

/**
 * Cancel a subscription
 * @param {string} subscriptionId - The ID of the subscription to cancel
 * @returns {Promise<boolean>} - Whether the cancellation was successful
 */
export const cancelSubscription = async (subscriptionId) => {
  try {
    if (!subscriptionId) {
      throw new Error("Subscription ID is required");
    }

    // This would typically call a Supabase Edge Function to cancel the subscription
    // For now, we'll just update the local subscription status
    const { error } = await supabase
      .from("subscriptions")
      .update({ status: "canceled", cancel_at_period_end: true })
      .eq("stripe_id", subscriptionId);

    if (error) {
      console.error("Error canceling subscription:", error);
      throw new Error(`Failed to cancel subscription: ${error.message}`);
    }

    return true;
  } catch (error) {
    console.error("Error in cancelSubscription:", error);
    toast.error(`Cancellation error: ${error.message}`);
    return false;
  }
};
